// Eigenbau: Benachrichtigungs-Inbox (Paket 4 / 16)
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Seitenkopf } from "@/components/Seitenkopf";
import { Leerzustand } from "@/components/Leerzustand";
import { Button } from "@/components/Button";
import { Badge } from "@/components/Badge";
import { Fehlerzeile } from "@/components/Fehlerzeile";
import { MehrLaden } from "@/components/MehrLaden";
import { api } from "@/lib/api";
import { fehlerMeldung, useCursorListe } from "@/lib/haken";

export function Benachrichtigungen() {
  const client = useQueryClient();
  const [nurUngelesene, setzeNurUngelesene] = useState(false);

  const liste = useCursorListe({
    queryKey: ["cubic", "benachrichtigungen", { nurUngelesene }],
    lade: (cursor) =>
      api.benachrichtigungen.liste.query({
        nurUngelesene,
        cursor,
        grenze: 50,
      }),
  });

  const gelesen = useMutation({
    mutationFn: (id: string) =>
      api.benachrichtigungen.gelesen.mutate({ id }),
    onSuccess: async () => {
      await client.invalidateQueries({
        queryKey: ["cubic", "benachrichtigungen"],
      });
    },
  });

  return (
    <>
      <Seitenkopf
        titel="Benachrichtigungen"
        beschreibung="Erwähnungen und Hinweise aus Projekten, die du sehen darfst."
      />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Button
          variant={nurUngelesene ? "secondary" : "primary"}
          onClick={() => setzeNurUngelesene(false)}
        >
          Alle
        </Button>
        <Button
          variant={nurUngelesene ? "primary" : "secondary"}
          onClick={() => setzeNurUngelesene(true)}
          data-testid="benachrichtigungen-nur-ungelesen"
        >
          Nur ungelesen
        </Button>
      </div>

      {liste.isLoading ? (
        <p className="text-sm text-gray-500">Lade Benachrichtigungen …</p>
      ) : liste.isError ? (
        <Fehlerzeile
          meldung={fehlerMeldung(liste.error)}
          onErneut={() => void liste.refetch()}
        />
      ) : liste.eintraege.length === 0 ? (
        <Leerzustand
          titel={
            nurUngelesene
              ? "Keine ungelesenen Benachrichtigungen."
              : "Keine Benachrichtigungen."
          }
          hinweis="Erwähnungen in Kommentaren erscheinen hier."
        />
      ) : (
        <ul className="space-y-2" data-testid="benachrichtigungen-liste">
          {liste.eintraege.map((b) => (
            <li
              key={b.id}
              className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-gray-200 p-3 dark:border-gray-800"
            >
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-50">
                  {b.anlass}
                  {!b.gelesenAm ? (
                    <Badge className="ml-2">ungelesen</Badge>
                  ) : null}
                </p>
                <p className="text-xs text-gray-500">
                  {b.betrifftArt} ·{" "}
                  {new Date(b.angelegtAm).toLocaleString("de-DE")}
                </p>
              </div>
              {!b.gelesenAm ? (
                <Button
                  variant="secondary"
                  disabled={gelesen.isPending}
                  onClick={() => gelesen.mutate(b.id)}
                  data-testid={`benachrichtigung-gelesen-${b.id}`}
                >
                  Als gelesen
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      <MehrLaden
        hatMehr={liste.hasNextPage}
        laedt={liste.isFetchingNextPage}
        laden={() => void liste.fetchNextPage()}
        beschriftung="Ältere Benachrichtigungen laden"
      />
    </>
  );
}
